import schedulerCard from "@/assets/blog/social-media-scheduling-tools-for-law-firms-card.webp.asset.json";
/**
 * Listing-page-only presentation data for the blog / resource hub.
 * This file adds cover images, topic labels and alt text for the existing
 * articles. It never changes article slugs, titles, content or URLs.
 */
import piMatters from "@/assets/blog/why-does-seo-for-personal-injury-lawyers-matter.jpg";
import strategy from "@/assets/blog/law-firm-seo-strategy.jpg";
import duiAsset from "@/assets/blog/dui-lawyer-seo-guide.webp.asset.json";
import soloAsset from "@/assets/blog/solo-lawyer-small-firm-seo.webp.asset.json";
import tips from "@/assets/blog/law-firm-seo-tips.jpg";
import tools from "@/assets/blog/best-law-firm-seo-tools.jpg";
import onPage from "@/assets/blog/on-page-seo-for-lawyers.jpg";
import statsAsset from "@/assets/blog/law-firm-seo-statistics-trends.webp.asset.json";
import keywordsAsset from "@/assets/blog/keyword-research-for-law-firms.webp.asset.json";
import agency from "@/assets/blog/how-to-choose-law-firm-seo-agency.jpg";
import geo from "@/assets/blog/generative-engine-optimization-geo-law-firms-2026.jpg";
import leadGenerationAsset from "@/assets/blog/can-seo-help-law-firms-generate-more-leads.webp.asset.json";
import turnSeoTrafficAsset from "@/assets/blog/how-law-firms-turn-seo-traffic-into-new-clients.webp.asset.json";

export const BLOG_CATEGORIES = [
  "All",
  "SEO Strategy",
  "Local SEO",
  "Practice Area SEO",
  "AI & Tools",
] as const;

export type BlogCategory = (typeof BLOG_CATEGORIES)[number];

type Visual = { image: string; category: Exclude<BlogCategory, "All">; alt: string };

export const BLOG_VISUALS: Record<string, Visual> = {
  "law-firm-performance-metrics": {
    image: "/images/blog/law-firm-performance-metrics/law-firm-performance-metrics-card.webp",
    category: "SEO Strategy",
    alt: "Law Firm Performance Metrics: The Metrics That Actually Matter",
  },
  "social-media-scheduling-tools-for-law-firms": {
    image: schedulerCard.url,
    category: "AI & Tools",
    alt: "Social Media Scheduling Tools for Small Law Firms: What Works",
  },
  "who-can-manage-google-business-profile-for-law-firm": {
    image: "/images/blog/who-can-manage-google-business-profile-for-law-firm/who-can-manage-google-business-profile-for-law-firm-card.webp",
    category: "Local SEO",
    alt: "Who Can Manage a Google Business Profile for a Law Firm?",
  },
  "how-to-use-technology-to-improve-legal-marketing": {
    image: "/images/blog/how-to-use-technology-to-improve-legal-marketing/how-to-use-technology-to-improve-legal-marketing-card.webp",
    category: "AI & Tools",
    alt: "How to Use Technology to Improve Legal Marketing Effectiveness",
  },
  "how-to-measure-roi-on-law-firm-marketing": {
    image: "/images/blog/how-to-measure-roi-on-law-firm-marketing/how-to-measure-roi-on-law-firm-marketing-card.webp",
    category: "SEO Strategy",
    alt: "How to Measure ROI on Law Firm Marketing Spend",
  },
  "how-to-generate-leads-for-law-firms": {
    image: "/images/blog/how-to-generate-leads-for-law-firms/how-to-generate-leads-for-law-firms-card.webp",
    category: "SEO Strategy",
    alt: "How Do Law Firms Capture Leads From Their Website?",
  },
  "how-law-firms-get-found-on-chatgpt": {
    image: "/images/blog/how-law-firms-get-found-on-chatgpt/how-law-firms-get-found-on-chatgpt-card.webp",
    category: "AI & Tools",
    alt: "How Law Firms Can Get Found When Clients Search on ChatGPT",
  },
  "how-law-firms-turn-seo-traffic-into-new-clients": {
    image: turnSeoTrafficAsset.url,
    category: "SEO Strategy",
    alt: "Dark navy desk scene with law books, scales of justice, laptop showing organic traffic growth, and a search-to-client conversion flow for law firms",
  },
  "can-seo-help-law-firms-generate-more-leads": {
    image: leadGenerationAsset.url,
    category: "SEO Strategy",
    alt: "Law firm SEO lead generation journey from search visibility to qualified traffic, trust, and enquiries",
  },
  "why-does-seo-for-personal-injury-lawyers-matter": {
    image: piMatters,
    category: "Practice Area SEO",
    alt: "Illustration of a rising search demand curve beside a personal injury claim file and scales of justice",
  },
  "law-firm-seo-strategy": {
    image: strategy,
    category: "SEO Strategy",
    alt: "Illustration of an ascending strategy roadmap of connected nodes and milestones",
  },
  "dui-lawyer-seo-guide": {
    image: duiAsset.url,
    category: "Practice Area SEO",
    alt: "Illustration of a city map grid with local search pins and a gavel for DUI defense SEO",
  },
  "solo-lawyer-small-firm-seo": {
    image: soloAsset.url,
    category: "SEO Strategy",
    alt: "Illustration of a single small law office building connected to a search visibility network",
  },
  "law-firm-seo-tips": {
    image: tips,
    category: "SEO Strategy",
    alt: "Illustration of an SEO checklist card with completed gold check marks",
  },
  "best-law-firm-seo-tools": {
    image: tools,
    category: "AI & Tools",
    alt: "Illustration of SEO dashboard panels and measurement dials arranged as a toolkit",
  },
  "on-page-seo-for-lawyers": {
    image: onPage,
    category: "SEO Strategy",
    alt: "Illustration of a webpage wireframe with highlighted heading and content blocks",
  },
  "law-firm-seo-statistics-trends": {
    image: statsAsset.url,
    category: "AI & Tools",
    alt: "Illustration of a bar chart with a rising trend arrow representing law firm SEO statistics",
  },
  "keyword-research-for-law-firms": {
    image: keywordsAsset.url,
    category: "SEO Strategy",
    alt: "Illustration of a search bar branching into a keyword research tree",
  },
  "how-to-choose-law-firm-seo-agency": {
    image: agency,
    category: "SEO Strategy",
    alt: "Illustration of two agency comparison cards with an evaluation checklist",
  },
  "top-geo-agencies-for-law-firms": {
    image: geo,
    category: "AI & Tools",
    alt: "Illustration of an AI answer interface panel with a spark icon and cited source lines",
  },
};

/** Cornerstone guides for the "Start Here" rail, in reading order. */
export const START_HERE_SLUGS = [
  "law-firm-seo-strategy",
  "on-page-seo-for-lawyers",
  "keyword-research-for-law-firms",
] as const;

/** Featured cornerstone guide shown at the top of the hub. */
export const FEATURED_SLUG = "law-firm-seo-strategy";
